import React from 'react';
import cn from 'classnames';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Carousel, { CarouselItem } from 'tbk-components/src/components/Carousel';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import ResourceCard, { IResourceCardProps } from '../ResourceCard';
import AnimatedArcs from '../AnimatedArcs';

export interface IResourceCardListingProps extends IBaseElementProps {
	title?: string;
	headingSize?: 'Small' | 'Large';
	/**
	 * Heading style 'Card' will only appear if listing style is in 'Grid'
	 */
	headingStyle?: 'Columned' | 'Centered' | 'Card';
	excerpt?: string;
	cta?: ILink;
	/**
	 * Enable this to get the latest resources  If this is enabled, ensure there are no resources entered below
	 */
	useAutomaticResources?: boolean;
	resources?: IResourceCardProps[];
	showTypeLabels?: boolean;
	showArcs?: boolean;
	hideCardExcerpts?: boolean;
	backgroundColor?: 'white' | 'light' | 'themed';
	listingStyle?: 'grid' | 'carousel' | 'list';
	arcPosition?: 'Left' | 'Right';
	allWide?: boolean;
	/**
	 * @control uniqueid
	 * @prefix card-listing
	 */
	uniqueId?: string;
	/**
	 * @noUI
	 */
	allSearchCards?: boolean;
	allTinyCards?: boolean;
	/**
	 * This only applies when listingStyle is set to 'grid'
	 */
	gridDesktopColumns?: 2 | 3 | 4;
	/**
	 * @noUI
	 */
	onFilterTagClick?: (event: any) => any;
}

/**
 * Resource Listing
 * @icon slides
 * @block
 */
const ResourceCardListing: React.FC<IResourceCardListingProps> = ({
	title,
	cta,
	useAutomaticResources,
	resources = [],
	showTypeLabels = false,
	backgroundColor = 'white',
	listingStyle = 'grid',
	headingSize = 'Small',
	headingStyle = 'Columned',
	showArcs = false,
	hideCardExcerpts = false,
	allWide = false,
	arcPosition = 'Right',
	id,
	className,
	classNames = [],
	uniqueId,
	gridDesktopColumns = 3,
	allTinyCards = false,
	excerpt,
	allSearchCards = false,
	onFilterTagClick,
}) => {
	// This component will also be used for Related Posts on the new cards.
	const arcTwo =
		backgroundColor === 'white'
			? arcPosition === 'Right'
				? 'var(--bs-primary)'
				: 'var(--bs-light)'
			: backgroundColor === 'light'
			? 'var(--bs-primary)'
			: 'var(--bs-light)';
	const arcOne =
		backgroundColor === 'white'
			? arcPosition === 'Right'
				? 'var(--bs-light)'
				: 'var(--bs-primary)'
			: backgroundColor === 'light'
			? 'var(--bs-light)'
			: 'var(--bs-light)';
	const arcColors = [arcOne, arcTwo];

	const genUniqueId =
		uniqueId || `card-listing-${Math.random().toString(36).substr(2, 9)}`;

	const defaultBreakpoints = {
		1920: {
			perView: 3.5,
			gap: 32,
		},

		1200: {
			perView: 3.25,
			gap: 32,
		},

		991: {
			perView: 2.5,
			gap: 16,
			after: 100,
		},

		768: {
			perView: 2.25,
			gap: 16,
			after: 100,
		},

		568: {
			perView: 1.5,
			gap: 16,
			after: 100,
		},

		380: {
			perView: 1.2,
			gap: 16,
			after: 100,
		},
	};

	const whitePaperBreakpoints = {
		1920: {
			perView: 2.25,
			gap: 32,
		},
		1440: {
			perView: 2.2,
			gap: 32,
		},

		991: {
			perView: 1.7,
			gap: 16,
			after: 100,
		},

		768: {
			perView: 1.25,
			gap: 16,
			after: 100,
		},

		568: {
			perView: 1.45,
			gap: 16,
			after: 100,
		},
	};

	const carouselOptions = {
		gap: 32,
		type: 'slider',
		rewind: false,
		bound: true,
		perView: allWide ? 2.25 : 3.35, // For huge screens >1920px
		breakpoints: allWide ? whitePaperBreakpoints : defaultBreakpoints,
	};

	const headerContent = (title || excerpt) && (
		<div className="resource-card-listing-text d-flex flex-column gap-3 mw-641px">
			{title && (
				<h2
					className={cn(
						'mb-0',
						headingSize === 'Small' && 'h3',
						backgroundColor === 'themed' && 'text-white',
					)}
				>
					{title}
				</h2>
			)}
			{excerpt && (
				<p
					className={cn(
						'mb-0',
						headingStyle == 'Card' &&
							listingStyle !== 'carousel' &&
							'text-body-lg',
					)}
				>
					{excerpt}
				</p>
			)}
		</div>
	);
	return (
		<div
			id={id}
			className={cn(
				'resource-card-listing',
				className || [
					'py-8',
					listingStyle !== 'carousel' ? 'px-3' : 'px-0',
					`bg-${backgroundColor}`,
					backgroundColor === 'themed' &&
						'theme-bg-radial-gradient-one',
					'position-relative',
				],
				...classNames,
			)}
		>
			<div className="resource-card-listing__container z-1 position-relative mw-1240px w-100 p-0 mx-auto">
				{(title || cta?.label) && (
					<div
						className={cn(
							'resource-card-listing__head mb-6 px-5 w-100 mx-auto d-inline-flex justify-content-between align-items-start gap-3 flex-wrap',
							headingStyle === 'Centered' &&
								'flex-column text-center align-items-center mx-auto mb-4 mb-md-9',
						)}
					>
						{(headingStyle !== 'Card' ||
							listingStyle === 'carousel') &&
							headerContent}
						{cta?.label && (
							<Button
								size="sm"
								type={
									backgroundColor === 'themed'
										? 'light'
										: 'primary'
								}
								{...cta}
							/>
						)}
					</div>
				)}
				<div
					className={cn(
						'resource-card-listing__cards',
						`resource-card-listing__cards--${listingStyle}`,
						`resource-card-listing__cards--${gridDesktopColumns}-columns`,
						resources?.length === 3 &&
							listingStyle === 'grid' &&
							'resource-card-listing--exactly-three',
					)}
				>
					{listingStyle !== 'carousel' ? (
						<>
							{headingStyle === 'Card' && headerContent}
							{resources?.map((resource, index) => (
								<ResourceCard
									allWide={allWide}
									tinyCard={allTinyCards}
									allSearchList={allSearchCards}
									showTypeLabel={showTypeLabels}
									hideCardExcerpt={hideCardExcerpts}
									key={index}
									{...resource}
									onFilterTagClick={
										onFilterTagClick
											? onFilterTagClick
											: null
									}
								/>
							))}
						</>
					) : (
						<Carousel
							className={cn(
								'resource-card-listing__carousel-glide',
							)}
							id={genUniqueId}
							perView={3.5}
							showBullets={false}
							options={carouselOptions}
						>
							{resources?.map((resource, index) => (
								<CarouselItem key={index}>
									<ResourceCard
										allWide={allWide}
										tinyCard={allTinyCards}
										allSearchList={allSearchCards}
										showTypeLabel={showTypeLabels}
										hideCardExcerpt={hideCardExcerpts}
										key={index}
										{...resource}
									/>
								</CarouselItem>
							))}
						</Carousel>
					)}
				</div>
			</div>
			{showArcs && (
				<AnimatedArcs
					arcsStyle="Hero - Three Lines"
					arcsPosition={arcPosition}
					arcOneColor={arcColors[0]}
					arcOneStrokeWidth={4}
					arcTwoColor={arcColors[1]}
					arcTwoStrokeWidth={3}
				/>
			)}
		</div>
	);
};
export default ResourceCardListing;
